import NewStore from "./NewStore";
import { Goals } from "models/Goals";
import { action, observable } from "mobx";
import ApiRoutes from "routes/ApiRoutes";
import { GoalType } from "enums/GoalType";
import { JuiceSpeciesYield } from "models/dtos/JuiceSpeciesYieldDTO";
import { SpeciesYield } from "models/dtos/SpeciesYieldDTO";
import type { YieldDetailReport } from "models/dtos/YieldDetailReportDTO";
import moment from "moment";

export class GoalsStore extends NewStore<Goals> {
  private static _instance: GoalsStore;

  @observable goalsList: Goals[] = [];

  @observable goalByDate: Goals;

  @observable isFetchingList: boolean = false;

  @observable yieldReportURL: string = "";

  @observable yieldListByDateRange: {
    yieldData: SpeciesYield[];
    juiceYieldData: JuiceSpeciesYield[];
  };

  @observable sandReportUrl: string;

  @observable yieldDetailReport: YieldDetailReport;

  constructor() {
    super();
    Goals._store = this;
  }

  static getInstance(): GoalsStore {
    if (!this._instance) {
      this._instance = new GoalsStore();
    }
    return this._instance;
  }

  @action
  async fetchGoals(force?: boolean, params?: { [key: string]: any }) {
    try {
      if (this.goalsList.length > 0 && !force) {
        return;
      }
      this.isFetchingList = true;
      const response = await this.apiService.get(ApiRoutes.goals.list, params);
      if (force) {
        this.goalsList = response.data;
      } else {
        this.goalsList.push(...response.data);
      }
    } finally {
      this.isFetchingList = false;
    }
  }

  @action
  async fetchGoalByDate(params: { [key: string]: any }) {
    this.goalByDate = undefined;
    const response = await this.apiService.get(
      ApiRoutes.goals.getByDate,
      params,
    );
    this.goalByDate = response.data;
  }

  @action
  async createGoals(body: { [key: string]: any }) {
    const response = await this.apiService.post(ApiRoutes.goals.list, body);
    let found = 0;
    this.goalsList.forEach((element, index) => {
      if (element.date === body.date) {
        this.goalsList[index] = response.data;
        found = 1;
      }
    });
    if (found === 0) {
      this.goalsList.push(response.data);
    }
  }

  @action
  async fetchGoalsByDates(params: { [key: string]: any }) {
    const response = await this.apiService.get(
      ApiRoutes.goals.getByDates,
      params,
    );
    this.goalsList = response.data;
  }

  @action
  async fetchYieldSpreadsheetByDateRange(params: { [key: string]: any }) {
    const response = await this.apiService.get(
      ApiRoutes.reports.getYieldSpreadsheet,
      params,
    );
    this.yieldReportURL = response.url;
  }

  @action
  async fetchYieldListByDateRange(params: { [key: string]: any }) {
    const response = await this.apiService.get(
      ApiRoutes.reports.getYieldList,
      params,
    );
    this.yieldListByDateRange = response.data;
  }

  @action
  async fetchYieldDetailReport(params: { [key: string]: any }) {
    const response = await this.apiService.get(
      ApiRoutes.reports.getYieldDetailList,
      params,
    );
    this.yieldDetailReport = response.data;
  }

  @action
  async fetchSandReport(params: { [key: string]: any }) {
    const response = await this.apiService.get(
      ApiRoutes.reports.getSandSpreadsheet,
      params,
    );
    this.sandReportUrl = response.url;
  }

  @action
  async deleteGoal(id: number, goalType: GoalType, date: string) {
    const response = await this.apiService.delete(
      ApiRoutes.goals.deleteGoal(id),
      { type: goalType },
    );
    if (response.true) {
      const updatingGoalIndex = this.goalsList.findIndex(
        el => el.date === date,
      );
      const goal = this.goalsList[updatingGoalIndex];
      if (goalType === GoalType.BOAT) {
        const updatedBoatGoals = goal.boats.filter(el => el.id !== id);
        goal.boats = updatedBoatGoals;
      } else {
        const updatedCustomerGoals = goal.customers.filter(el => el.id !== id);
        goal.customers = updatedCustomerGoals;
      }
      this.goalsList.splice(updatingGoalIndex, 1, goal);
    }
  }


  @action
  async rescheduleGoal(goal: Goals, newDate: string) {
    if (!goal || !newDate || goal.date === newDate) {
      return;
    }

    const targetGoalDate = moment(newDate).startOf("day");

    const boatGoalIds = goal.boats
      .map(boat => boat.id)
      .filter((id): id is number => typeof id === "number");
    const customerGoalIds = goal.customers
      .map(customer => customer.id)
      .filter((id): id is number => typeof id === "number");

    const payload: { [key: string]: any } = {
      current_date: goal.date,
      date: newDate,
    };

    if (boatGoalIds.length > 0) {
      payload.boat_goal_ids = boatGoalIds;
    }
    if (customerGoalIds.length > 0) {
      payload.customer_goal_ids = customerGoalIds;
    }
    if (goal.dumping_goal?.id) {
      payload.dumping_goal_id = goal.dumping_goal.id;
    }

    await this.apiService.post(
      ApiRoutes.goals.schedule,
      payload,
    );

    const nextDateString = targetGoalDate.format("YYYY-MM-DD");

    const goalToUpdate = goal;
    goalToUpdate.date = nextDateString;

    if (boatGoalIds.length > 0) {
      for (let index = 0; index < goalToUpdate.boats.length; index += 1) {
        const boat = goalToUpdate.boats[index];
        if (boatGoalIds.includes(boat.id)) {
          boat.goal_for = nextDateString;
        }
      }
    }

    if (customerGoalIds.length > 0) {
      for (let index = 0; index < goalToUpdate.customers.length; index += 1) {
        const customer = goalToUpdate.customers[index];
        if (customerGoalIds.includes(customer.id)) {
          customer.goal_for = nextDateString;
        }
      }
    }

    if (payload.dumping_goal_id && goalToUpdate.dumping_goal) {
      goalToUpdate.dumping_goal.goal_for = nextDateString;
    }

    const filteredGoals = this.goalsList.filter(existingGoal => existingGoal !== goalToUpdate);
    const dedupedGoals = filteredGoals.filter(existingGoal => existingGoal.date !== nextDateString);

    this.goalsList = [...dedupedGoals, goalToUpdate];
    this.sortGoalsByDate();
  }

  @action
  async updateGoalVisibility(date: string, isHidden: boolean) {
    const response = await this.apiService.post(ApiRoutes.goals.updateVisibility, {
      date,
      is_hidden: isHidden,
    });

    const goal = this.goalsList.find(el => el.date === date);
    if (goal) {
      goal.is_hidden = response.data.is_hidden;
    }
    if (this.goalByDate?.date === date) {
      this.goalByDate.is_hidden = response.data.is_hidden;
    }
  }

  @action
  private sortGoalsByDate() {
    this.goalsList = [...this.goalsList].sort(
      (first, second) =>
        new Date(first.date).getTime() - new Date(second.date).getTime(),
    );
  }
}
